// src/components/position-eligibility/PlayerEligibilityModal.js
import React, { useState, useEffect } from 'react';
import { X, Shield, Info, CheckCircle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import PlayerLink from '../PlayerLink';

// All valid slot types (without index suffixes)
const VALID_SLOT_TYPES = ['C', '1B', '2B', '3B', 'SS', 'OF', 'CI', 'MI', 'UTIL', 'P'];

/**
 * Modal for editing player's custom position eligibility
 * Commissioner only
 */
const PlayerEligibilityModal = ({ player, league, onSave, onClose }) => {
  const [customEligibility, setCustomEligibility] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form with existing values
  useEffect(() => {
    if (player) {
      setCustomEligibility(player.league_context?.roster?.custom_eligibility || []);
      setNotes(player.league_context?.roster?.position_override_notes || '');
    }
  }, [player]);

  if (!player) return null;

  const togglePosition = (pos) => {
    if (customEligibility.includes(pos)) {
      setCustomEligibility(customEligibility.filter(p => p !== pos));
    } else {
      setCustomEligibility([...customEligibility, pos]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      await onSave({
        league_player_id: player.ids?.league_player,
        custom_eligibility: customEligibility.length > 0 ? customEligibility : null,
        notes: notes.trim() || null
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update eligibility');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);

    try {
      await onSave({
        league_player_id: player.ids?.league_player,
        custom_eligibility: null,
        notes: null
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to reset eligibility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${dynastyTheme.components.card.base} max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-700">
          <div className="flex items-center gap-3">
            <Shield className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
            <div>
              <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
                Edit Position Eligibility
              </h2>
              <p className={dynastyTheme.classes.text.neutralLight}>
                <PlayerLink mlbId={player.ids?.mlb} name={player.info?.full_name} style={{ color: '#9ca3af' }} /> ({player.info?.position})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${dynastyTheme.classes.text.neutralLight} hover:bg-neutral-700 ${dynastyTheme.classes.transition}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info Banner */}
          <div className={`${dynastyTheme.components.card.highlighted} p-4`}>
            <div className="flex items-start gap-3">
              <Info className={`w-5 h-5 ${dynastyTheme.classes.text.info} flex-shrink-0 mt-0.5`} />
              <div className="space-y-1">
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  Grant this player eligibility for additional positions beyond their MLB listing.
                  This is typically used for minor league call-ups who played different positions
                  in the minors.
                </p>
                <p className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
                  Example: Oneil Cruz plays OF in MLB but extensively played SS in minors
                </p>
              </div>
            </div>
          </div>

          {/* Current Status */}
          <div className="space-y-2">
            <label className={dynastyTheme.components.label}>
              Current Slot Assignment:
            </label>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-2 rounded ${dynastyTheme.components.badge.neutral}`}>
                {player.league_context?.roster?.position || 'BENCH'}
              </span>
              <span className={dynastyTheme.classes.text.neutralDark}>
                (MLB Position: {player.info?.position})
              </span>
            </div>
          </div>

          {/* Position Selection */}
          <div className="space-y-3">
            <label className={dynastyTheme.components.label}>
              Grant Custom Eligibility For:
            </label>
            <div className="grid grid-cols-5 gap-2">
              {VALID_SLOT_TYPES.map(pos => {
                const isSelected = customEligibility.includes(pos);
                return (
                  <button
                    key={pos}
                    onClick={() => togglePosition(pos)}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${
                      isSelected
                        ? `${dynastyTheme.components.badge.primary} border-2 border-yellow-400 shadow-lg`
                        : `${dynastyTheme.components.badge.neutral} border-2 border-transparent hover:border-neutral-600`
                    }`}
                  >
                    {pos}
                    {isSelected && (
                      <CheckCircle className="w-4 h-4 inline ml-1" />
                    )}
                  </button>
                );
              })}
            </div>
            <p className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
              Selected positions: {customEligibility.length === 0 
                ? 'None (will use MLB default)' 
                : customEligibility.join(', ')
              }
            </p>
          </div>

          {/* Commissioner Notes */}
          <div className="space-y-2">
            <label className={dynastyTheme.components.label}>
              Commissioner Notes:
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Explain why custom eligibility was granted (e.g., 'Played SS in minors')"
              rows={3}
              className={dynastyTheme.components.input + ' w-full resize-none'}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className={dynastyTheme.classes.text.error}>
                {error}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-neutral-700 bg-neutral-800/50">
          <button
            onClick={handleReset}
            disabled={saving || (!player.league_context?.roster?.custom_eligibility && !player.league_context?.roster?.position_override_notes)}
            className={dynastyTheme.utils.getComponent('button', 'danger', 'md') + ' disabled:opacity-50 disabled:cursor-not-allowed'}
          >
            Reset to Default
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerEligibilityModal;
